import * as React from 'react';
import usePrevious from '@fuse/hooks/usePrevious';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import FuseSplashScreen from '@fuse/core/FuseSplashScreen';
import { getGroups } from 'app/store/groupsSlice';
import { fetchClients } from 'app/store/clientsSlice';
import { getConnections } from 'app/store/connectionsSlice';
import { subscribeToConnections } from 'app/store/subscriptionSlice';
import { getExternalMessages } from 'app/store/externalMessageSlice';
import { fetchChannel } from 'app/store/channelsSlice';
import { fetchSystemConfig } from 'app/store/homeSlice';
import { fetchReferrals } from 'app/store/referralsSlice';
import { fetchInterests } from 'app/store/referralInterestSlice';
import { fetchAuthorizedClients } from 'app/store/clientsAuthorizedSlice';
import { fetchMyOrganizationReferrals } from 'app/store/organizationReferralsSlice';
import { bulkLoginCalls,bulkBackgroundCalls } from 'app/shared-components/util/bulkApi';
import { warningMessage, clearMessage } from 'app/shared-components/util/message';
import { selectUser, setUserInitialized, fetchSubscriptionStatus } from 'app/store/userSlice';
import { selectConnectionStatus, checkConnectionStatus, selectPreviousConnectionStatus } from 'app/store/subscriptionSlice';
import { getAssignedCases } from 'app/store/supportSlice';
import { useSnackbar } from 'notistack';
import { useAuth } from './auth/AuthContext';
import useInterval from 'app/shared-components/util/useInterval';
import marketingConfigs from './main/marketing/marketingConfig';



function AppInitialization({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const user= useSelector(selectUser);
  const connectionStatus= useSelector(selectConnectionStatus);
  const previousStatus= useSelector(selectPreviousConnectionStatus);
  const userId= user?.id;
  const isAuthenticated= useAuth().isAuthenticated;
  const isInitialized= user?.initialized;
  const [bgDataLoaded, setBgDataLoaded] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const statusInterval= 15000; //15 seconds (very lightweight call)
  const [startCheck, stopCheck]= useInterval(checkStatus, statusInterval);

  const isPublicRoute = useMemo(() => {
    const currentPath = location.pathname;
    return marketingConfigs.some(config => 
      config.auth === null && 
      config.routes?.some(route => {
        const routePath = route.path.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^/${routePath}$`);
        return regex.test(currentPath);
      })
    );
  }, [location.pathname]);

  function checkStatus(){
     dispatch(checkConnectionStatus());
  }

  useEffect(() => {
    if (previousStatus !== "Connected" && connectionStatus === "Connected"){
    	 clearMessage(dispatch);
    }
    else if (previousStatus === "Connected" && connectionStatus && connectionStatus !== "Connected"){
    	 warningMessage(dispatch, "App is disconnected; please check your network.", 60000);
    }
  }, [connectionStatus]);
   
  useEffect(() => {
	  if (!isAuthenticated || userId === '1') {
	    stopCheck();
	    return;
	  }

	  if (isInitialized || isPublicRoute) {
		  return;
	  }
  	
	  //Use bulk load to get required data
	  bulkLoginCalls({user: user}).then((bulk) => {
				 const bulkData= bulk.items;
				 const subscriptionStatus= bulkData['subscription'];
				 const userTimeline= bulkData['userTimeline'];
				 const systemConfig= bulkData['systemConfig'];
				 const groups= bulkData['groups'];
				 const teams= bulkData['teams'];
				 const organizations= bulkData['organizations'];
				 const clients= bulkData['clients'];
				 const connections= bulkData['connections'];
				 const messages= bulkData['messages'];
				 const referrals= bulkData['referrals'];
				 const referralInterests= bulkData['referralInterests'];
				 const authorizedClients= bulkData['authorizedClients'];

		 Promise.all([
		  dispatch(fetchChannel({channelId: user.timelineId, bulk: userTimeline.code == 200 ? userTimeline.body : undefined })),
		  dispatch(fetchSystemConfig({bulk: systemConfig.code == 200 ? systemConfig.body : undefined })),
			dispatch(getGroups({type: "group", bulk: groups.code == 200 ? groups.body : undefined })),
			dispatch(getGroups({type: "team", closeSnackbar: closeSnackbar, enqueueSnackbar: enqueueSnackbar, bulk: teams.code == 200 ? teams.body : undefined})),
			dispatch(getGroups({type: "organization", closeSnackbar: closeSnackbar, enqueueSnackbar: enqueueSnackbar, bulk: organizations.code == 200 ? organizations.body : undefined})),
			dispatch(fetchClients({ bulk: clients.code == 200 ? clients.body : undefined })),
			dispatch(getConnections({ bulk: connections.code == 200 ? connections.body : undefined })),
			dispatch(getExternalMessages({ bulk: messages.code == 200 ? messages.body : undefined })),
			dispatch(fetchReferrals({ status: "open", bulk: referrals.code == 200 ? referrals.body : undefined })),
			dispatch(fetchInterests({ status: "open", bulk: referralInterests.code == 200 ? referralInterests.body : undefined })),
			dispatch(fetchAuthorizedClients({ bulk: authorizedClients.code == 200 ? authorizedClients.body : undefined })),
			dispatch(fetchSubscriptionStatus({ bulk: subscriptionStatus?.code == 200 ? subscriptionStatus.body : undefined })),
			dispatch(subscribeToConnections()),
		  ]).then((values) => {
			dispatch(setUserInitialized());
			startCheck();
		  });
	  });
   }, [userId, isAuthenticated, isInitialized, isPublicRoute]);
   
   useEffect(() => {
   	if (isAuthenticated && isInitialized && !bgDataLoaded && !isPublicRoute)
	  //Use bulk load to load background data after login
      bulkBackgroundCalls({user: user}).then((bulk) => {
		 const bulkData= bulk.items;
		 const support= bulkData['support'];
		 const myOrgReferrals= bulkData['myOrgReferrals'];

	     Promise.all([
	        dispatch(fetchMyOrganizationReferrals({ bulk: myOrgReferrals.code == 200 ? myOrgReferrals.body : undefined })), 	
	        dispatch(getAssignedCases({closeSnackbar: closeSnackbar, enqueueSnackbar: enqueueSnackbar, bulk: support.code == 200 ? support.body : undefined })),        
	      ]).then((values) => {
	        setBgDataLoaded(true);
	      });	
	  });	  
   }, [userId, isAuthenticated, isInitialized, bgDataLoaded, isPublicRoute]);
 
  return !isAuthenticated || isInitialized || isPublicRoute ? (
	children
  ) : (
    <FuseSplashScreen />
  );
}


export default AppInitialization;
